import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { getFirebaseWebFirestore } from "./firebaseWeb";
import { buildDefaultPrivatePolicy } from "./userPolicyRepository";

const PUBLIC_PROFILE_COLLECTION = "webUserProfiles";
const PRIVATE_PROFILE_COLLECTION = "webUserPrivateProfiles";
const SIGNUP_LEGAL_VERSION = "2026-06-04";
const SIGNUP_LEGAL_KEYS = ["terms-of-service", "community-rules", "kvkk-notice"];

function providerIds(user) {
  return Array.isArray(user?.providerData)
    ? user.providerData.map((provider) => provider.providerId).filter(Boolean)
    : [];
}

function cleanEmail(email) {
  return String(email || "").trim().toLocaleLowerCase("tr-TR");
}

function cleanAlias(value) {
  const alias = String(value || "").trim().replace(/^@+/, "").toLocaleLowerCase("tr-TR");
  return /^[a-z0-9][a-z0-9._-]{2,31}$/i.test(alias) ? alias : "";
}

export function isUserEmailVerified(user) {
  return Boolean(user?.emailVerified);
}

export function isUserPhoneVerified(user, account = {}) {
  return Boolean(user?.phoneNumber || account?.phoneVerified || account?.phoneNumberVerifiedAt);
}

export async function getUserPrivateAccount(uid) {
  if (!uid) return null;
  const db = getFirebaseWebFirestore();
  const snapshot = await getDoc(doc(db, PRIVATE_PROFILE_COLLECTION, uid));
  return snapshot.exists() ? snapshot.data() || null : null;
}

function hasAcceptedRequiredSignupConsents(consents = {}) {
  return Boolean(
    consents.signupTermsAccepted
      && consents.communityRulesAccepted
      && consents.kvkkNoticeAccepted
      && consents.requiredSignupAccepted,
  );
}

function buildSignupLegalConsents(existing = {}, legalConsent = null) {
  if (hasAcceptedRequiredSignupConsents(existing)) return existing;
  if (!legalConsent?.accepted) return existing || {};
  return {
    ...(existing || {}),
    requiredSignupAccepted: true,
    signupTermsAccepted: true,
    communityRulesAccepted: true,
    kvkkNoticeAccepted: true,
    acceptedLegalKeys: SIGNUP_LEGAL_KEYS,
    signupLegalVersion: SIGNUP_LEGAL_VERSION,
    signupConsentSource: legalConsent.source || "denkup-web-signup",
    signupConsentPath: legalConsent.path || "/giris",
    signupAcceptedAt: serverTimestamp(),
  };
}

function buildInitialOnboardingStatus(existing = {}) {
  if (existing?.onboardingStatus) return existing.onboardingStatus;
  return {
    profileRequired: true,
    profileCompleted: false,
    requiredSteps: ["profile-basics", "interests", "privacy"],
    startedAt: serverTimestamp(),
    completedAt: null,
  };
}

export async function syncUserAccountProfile(user, options = {}) {
  if (!user?.uid) return null;
  const db = getFirebaseWebFirestore();

  const publicRef = doc(db, PUBLIC_PROFILE_COLLECTION, user.uid);
  const privateRef = doc(db, PRIVATE_PROFILE_COLLECTION, user.uid);
  const [publicSnapshot, privateSnapshot] = await Promise.all([getDoc(publicRef), getDoc(privateRef)]);
  const existingPublic = publicSnapshot.exists() ? publicSnapshot.data() || {} : {};
  const existingPrivate = privateSnapshot.exists() ? privateSnapshot.data() || {} : {};
  const emailVerified = isUserEmailVerified(user);
  const phoneVerified = isUserPhoneVerified(user, existingPrivate);
  const phoneNumber = user.phoneNumber || existingPrivate.phoneNumber || null;
  const privatePolicy = buildDefaultPrivatePolicy(existingPrivate);
  const legalConsents = buildSignupLegalConsents(existingPrivate.legalConsents, options.legalConsent);
  const isNewPrivateProfile = !privateSnapshot.exists();

  if (isNewPrivateProfile && !hasAcceptedRequiredSignupConsents(legalConsents)) {
    throw new Error("Hesap oluşturmak için Kullanıcı Sözleşmesi, Topluluk Kuralları ve KVKK bilgilendirmesi kabul edilmelidir.");
  }

  const publicPayload = {
    uid: user.uid,
    profileVisibility: existingPublic.profileVisibility || "Public",
    nameVisibility: existingPublic.nameVisibility || {
      showFirstName: false,
      showLastName: false,
    },
    locationVisibility: existingPublic.locationVisibility || "Approximate",
    updatedAt: serverTimestamp(),
    createdAt: existingPublic.createdAt || serverTimestamp(),
    metadata: {
      ...(existingPublic.metadata || {}),
      schemaVersion: "user-profile-v1",
      updatedBy: "auth-sync",
    },
  };
  if (user.displayName) publicPayload.displayName = user.displayName;
  const profileAlias = cleanAlias(existingPublic.alias || existingPublic.profileAlias || existingPublic.nickname || user.displayName);
  if (profileAlias) {
    publicPayload.alias = profileAlias;
    publicPayload.profileAlias = existingPublic.profileAlias || profileAlias;
  }
  if (existingPublic.metadata?.updatedBy !== "auth-sync" && existingPublic.city) publicPayload.city = existingPublic.city;
  if (existingPublic.metadata?.updatedBy !== "auth-sync" && existingPublic.country) publicPayload.country = existingPublic.country;

  await Promise.all([
    setDoc(publicRef, publicPayload, { merge: true }),
    setDoc(privateRef, {
      uid: user.uid,
      email: cleanEmail(user.email),
      emailVerified,
      ...(emailVerified && !existingPrivate.emailVerifiedAt ? { emailVerifiedAt: serverTimestamp() } : {}),
      phoneNumber,
      phoneVerified,
      ...(phoneVerified && !existingPrivate.phoneNumberVerifiedAt ? { phoneNumberVerifiedAt: serverTimestamp() } : {}),
      authProviders: providerIds(user),
      accountStatus: emailVerified ? "Active" : "PendingVerification",
      legalConsents,
      onboardingStatus: buildInitialOnboardingStatus(existingPrivate),
      ...privatePolicy,
      lastLoginAt: serverTimestamp(),
      lastActiveAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdAt: existingPrivate.createdAt || serverTimestamp(),
    }, { merge: true }),
  ]);

  return getUserPrivateAccount(user.uid);
}
